import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box, Typography, Button, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Snackbar, Alert, Paper
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import api, { API_BASE_URL } from "../../utils/api";

const ArchitecturalRequestManager = () => {
  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ type: "", id: null });
  const [subtypeDialog, setSubtypeDialog] = useState(false);
  const [editingSubtype, setEditingSubtype] = useState(null);
  const [subtypeForm, setSubtypeForm] = useState({ subtype_id: null, type_id: null, subtype_name: "" });

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    fetchTypes();
    fetchSubtypes();
  }, []);

  const fetchTypes = async () => {
    const res = await api.get(`/architectural-request-types`);
    setTypes(res.data);
  };

  const fetchSubtypes = async () => {
    const res = await api.get(`/architectural-request-subtypes`);
    setSubtypes(res.data);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const handleInputChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const openAddDialog = (type = null) => {
    setFormData(type ? { ...type } : { type: "", id: null });
    setIsEditing(!!type);
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setFormData({ type: "", id: null });
    setIsEditing(false);
  };

  const handleFormSubmit = async () => {
    try {
      if (isEditing) {
        await api.put(`/architectural-request-types/${formData.id}`, formData);
        showSnackbar("Type updated!");
      } else {
        await api.post(`/architectural-request-types`, formData);
        showSnackbar("Type added!");
      }
      fetchTypes();
      closeDialog();
    } catch (err) {
      showSnackbar("Error saving type", "error");
    }
  };

  const confirmDeleteType = async (id) => {
    const relatedSubtypes = subtypes.filter(s => s.type_id === id);
    const typeLabel = types.find(t => t.id === id)?.type;

    const warning = `Deleting "${typeLabel}" will also remove ${relatedSubtypes.length} subtypes. Continue?`;

    if (window.confirm(warning)) {
      try {
        await api.delete(`/architectural-request-types/${id}`);
        showSnackbar("Type and subtypes deleted!");
        fetchTypes();
        fetchSubtypes();
      } catch (err) {
        showSnackbar("Error deleting type", "error");
      }
    }
  };

  const openSubtypeDialog = (type_id) => {
    setSubtypeForm({ id: null, type_id, subtype_name: "" });
    setEditingSubtype(null);
    setSubtypeDialog(true);
  };

  const editSubtype = (subtype) => {
    setSubtypeForm(subtype);
    setEditingSubtype(subtype.subtype_id);
    setSubtypeDialog(true);
  };

  const handleSubtypeChange = (e) => {
    setSubtypeForm({ ...subtypeForm, [e.target.name]: e.target.value });
  };

  const handleSubtypeSubmit = async () => {
    try {
      if (editingSubtype) {
        await api.put(`/architectural-request-subtypes/${editingSubtype}`, subtypeForm);
        showSnackbar("Subtype updated!");
      } else {
        await api.post(`/architectural-request-subtypes`, subtypeForm);
        showSnackbar("Subtype added!");
      }
      fetchSubtypes();
      setSubtypeDialog(false);
    } catch (err) {
      showSnackbar("Error saving subtype", "error");
    }
  };

  const deleteSubtype = async (id) => {
    if (!window.confirm("Delete this subtype?")) return;
    try {
      await api.delete(`/architectural-request-subtypes/${id}`);
      showSnackbar("Subtype deleted!");
      fetchSubtypes();
    } catch (err) {
      showSnackbar("Error deleting subtype", "error");
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", mt: 4 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        🏛 Architectural Request Types & Subtypes
      </Typography>

      <Button variant="contained" onClick={() => openAddDialog()} sx={{ mb: 2 }}>
        ➕ Add Type
      </Button>

      <Paper elevation={3}>
        <Table>
          <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Subtypes</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {types.map((type) => (
              <TableRow key={type.id}>
                <TableCell>{type.type}</TableCell>
                <TableCell>
                  {subtypes.filter(s => s.type_id === type.id).map(s => (
                    <Box key={s.subtype_id} sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2">— {s.subtype_name}</Typography>
                      <Box>
                        <IconButton size="small" onClick={() => editSubtype(s)}><Edit fontSize="inherit" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => deleteSubtype(s.subtype_id)}><Delete fontSize="inherit" /></IconButton>
                      </Box>
                    </Box>
                  ))}
                  <Button size="small" onClick={() => openSubtypeDialog(type.id)} sx={{ mt: 1 }}>
                    ➕ Add Subtype
                  </Button>
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => openAddDialog(type)}><Edit /></IconButton>
                  <IconButton color="error" onClick={() => confirmDeleteType(type.id)}><Delete /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Type Dialog */}
      <Dialog open={openDialog} onClose={closeDialog} fullWidth>
        <DialogTitle>{isEditing ? "Edit" : "Add"} Type</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Type" name="type" value={formData.type} onChange={handleInputChange} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleFormSubmit} variant="contained">{isEditing ? "Update" : "Add"}</Button>
        </DialogActions>
      </Dialog>

      {/* Subtype Dialog */}
      <Dialog open={subtypeDialog} onClose={() => setSubtypeDialog(false)} fullWidth>
        <DialogTitle>{editingSubtype ? "Edit" : "Add"} Subtype</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Subtype Name"
            name="subtype_name"
            value={subtypeForm.subtype_name}
            onChange={handleSubtypeChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubtypeDialog(false)}>Cancel</Button>
          <Button onClick={handleSubtypeSubmit} variant="contained">{editingSubtype ? "Update" : "Add"}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={closeSnackbar}>
        <Alert severity={snackbar.severity} onClose={closeSnackbar}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ArchitecturalRequestManager;
